/**
 * CampaignComposer component for composing and sending notification campaigns
 * Redesigned to match ListsManager and TemplatesManager styling
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Users, FileText, Mail, MessageSquare, Code, AlertCircle, CheckCircle, Variable, X, AlertTriangle, Plus, ChevronDown, Check } from 'lucide-react';
import {
  NotificationList,
  NotificationTemplate,
  NotificationChannel,
  NotificationCampaign,
  ListPreview,
  TemplateVariables,
  CampaignSendRequest,
  getLists,
  getTemplates,
  getTemplateVariables,
  previewCampaignRecipients,
  sendCampaign,
  getChannelLabel,
} from '../../utils/notificationsBroadcastApi';

interface CampaignComposerProps {
  onCampaignSent?: (campaign: NotificationCampaign) => void;
}

export const CampaignComposer: React.FC<CampaignComposerProps> = ({ onCampaignSent }) => {
  const navigate = useNavigate();
  const [lists, setLists] = useState<NotificationList[]>([]);
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [variables, setVariables] = useState<TemplateVariables | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ListPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Form state
  // Default to null (no selection) instead of '' (all customers) - sending to all is dangerous
  const [selectedListId, setSelectedListId] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [channel, setChannel] = useState<NotificationChannel>('email_html');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sentCampaign, setSentCampaign] = useState<NotificationCampaign | null>(null);
  
  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  // Refs for inputs to track cursor position and show popup
  const messageTextareaRef = useRef<HTMLTextAreaElement>(null);
  const subjectInputRef = useRef<HTMLInputElement>(null);
  const cursorPositionRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });
  const subjectCursorRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });
  
  // Track which input is focused for the variable popup
  const [focusedInput, setFocusedInput] = useState<'subject' | 'message' | null>(null);
  
  // Custom dropdown states
  const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false);
  const templateDropdownRef = useRef<HTMLDivElement>(null);
  const [audienceDropdownOpen, setAudienceDropdownOpen] = useState(false);
  const audienceDropdownRef = useRef<HTMLDivElement>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      const [listsData, templatesData, varsData] = await Promise.all([
        getLists(),
        getTemplates(),
        getTemplateVariables(),
      ]);
      setLists(listsData);
      setTemplates(templatesData);
      setVariables(varsData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Load preview when list or channel changes
  useEffect(() => {
    const loadPreview = async () => {
      setPreviewLoading(true);
      try {
        const previewData = await previewCampaignRecipients(
          channel,
          selectedListId || undefined
        );
        setPreview(previewData);
      } catch (err) {
        console.error('Failed to load preview:', err);
        setPreview(null);
      } finally {
        setPreviewLoading(false);
      }
    };

    loadPreview();
  }, [selectedListId, channel]);

  // Apply template when selected
  useEffect(() => {
    if (selectedTemplateId) {
      const template = templates.find((t) => t.id === selectedTemplateId);
      if (template) {
        setChannel(template.channel);
        setSubject(template.subject || '');
        setBody(template.body);
      }
    }
  }, [selectedTemplateId, templates]);

  // Clear selected template if it doesn't match the current channel
  useEffect(() => {
    if (selectedTemplateId) {
      const template = templates.find((t) => t.id === selectedTemplateId);
      if (template && template.channel !== channel) {
        setSelectedTemplateId('');
      }
    }
  }, [channel, templates, selectedTemplateId]);

  // Handle escape key for modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showConfirmModal) {
        setShowConfirmModal(false);
      }
      if (e.key === 'Escape' && templateDropdownOpen) {
        setTemplateDropdownOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showConfirmModal, templateDropdownOpen]);

  // Close template dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(e.target as Node)) {
        setTemplateDropdownOpen(false);
      }
    };
    if (templateDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [templateDropdownOpen]);

  // Close audience dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (audienceDropdownRef.current && !audienceDropdownRef.current.contains(e.target as Node)) {
        setAudienceDropdownOpen(false);
      }
    };
    if (audienceDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [audienceDropdownOpen]);

  const insertVariable = (varName: string) => {
    const variable = `{{${varName}}} `; // Trailing space for convenience
    
    if (focusedInput === 'subject') {
      // Insert into subject
      const { start, end } = subjectCursorRef.current;
      setSubject((prev) => {
        const before = prev.substring(0, start);
        const after = prev.substring(end);
        return before + variable + after;
      });
      
      setTimeout(() => {
        const input = subjectInputRef.current;
        if (input) {
          input.focus();
          const newCursorPos = start + variable.length;
          input.setSelectionRange(newCursorPos, newCursorPos);
          subjectCursorRef.current = { start: newCursorPos, end: newCursorPos };
        }
      }, 0);
    } else {
      // Insert into message body (default)
      const { start, end } = cursorPositionRef.current;
      setBody((prev) => {
        const before = prev.substring(0, start);
        const after = prev.substring(end);
        return before + variable + after;
      });
      
      setTimeout(() => {
        const textarea = messageTextareaRef.current;
        if (textarea) {
          textarea.focus();
          const newCursorPos = start + variable.length;
          textarea.setSelectionRange(newCursorPos, newCursorPos);
          cursorPositionRef.current = { start: newCursorPos, end: newCursorPos };
        }
      }, 0);
    }
  };

  // Handle channel change with smart content preservation
  const handleChannelChange = (newChannel: NotificationChannel) => {
    const previousChannel = channel;
    
    // If switching FROM HTML to anything else, clear the body (HTML won't work as plain text)
    if (previousChannel === 'email_html' && newChannel !== 'email_html') {
      setBody('');
      setSubject('');
    }
    // If switching TO HTML from anything else, clear the body (plain text won't work as HTML)
    else if (previousChannel !== 'email_html' && newChannel === 'email_html') {
      setBody('');
    }
    // If switching between SMS and Plain Email, keep content if within SMS bounds (480 chars = ~3 segments)
    else if (
      (previousChannel === 'sms' && newChannel === 'email_plain') ||
      (previousChannel === 'email_plain' && newChannel === 'sms')
    ) {
      // Keep the content - it's compatible between these types
      // Only clear if going TO SMS and content is way too long (more than 3 SMS segments)
      if (newChannel === 'sms' && body.length > 480) {
        // Optionally truncate or warn, but for now just keep it
        // User can see the character count warning
      }
    }
    
    setChannel(newChannel);
  };

  const handleSendClick = () => {
    if (selectedListId === null) {
      setError('Please select an audience before sending');
      return;
    }
    if (!body.trim()) {
      setError('Message body is required');
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmSend = async () => {
    setShowConfirmModal(false);
    setSending(true);
    setError(null);

    const data: CampaignSendRequest = {
      list_id: selectedListId || undefined,
      template_id: selectedTemplateId || undefined,
      channel,
      subject: channel !== 'sms' ? subject : undefined,
      body,
    };

    try {
      const campaign = await sendCampaign(data);
      setSentCampaign(campaign);
      onCampaignSent?.(campaign);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send campaign');
    } finally {
      setSending(false);
    }
  };

  const resetForm = () => {
    setSelectedListId('');
    setSelectedTemplateId('');
    setChannel('email_html');
    setSubject('');
    setBody('');
    setSentCampaign(null);
    setError(null);
  };

  const getChannelInfo = useCallback(() => {
    switch (channel) {
      case 'sms':
        return { icon: MessageSquare, label: 'SMS', color: 'green' };
      case 'email_plain':
        return { icon: Mail, label: 'Plain Email', color: 'blue' };
      case 'email_html':
        return { icon: Code, label: 'HTML Email', color: 'blue' };
      default:
        return { icon: Mail, label: 'Email', color: 'blue' };
    }
  }, [channel]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Show success state after sending
  if (sentCampaign) {
    const isSending = sentCampaign.status === 'sending';
    
    return (
      <div className="max-w-xl mx-auto p-6">
        <div className="border border-gray-200 dark:border-dark-border rounded-lg p-6">
          <div className="flex items-start gap-3 mb-4">
            {isSending ? (
              <div className="w-5 h-5 mt-0.5 flex-shrink-0">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent" />
              </div>
            ) : (
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            )}
            <div>
              <h2 className="text-base font-medium text-gray-900 dark:text-dark-text-primary">
                {isSending ? 'Campaign is sending' : 'Campaign sent successfully'}
              </h2>
              <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-1">
                {isSending ? (
                  <>
                    Sending to {sentCampaign.total_recipients} recipient{sentCampaign.total_recipients !== 1 ? 's' : ''}
                    {sentCampaign.opted_out_count > 0 && <span> · {sentCampaign.opted_out_count} opted out</span>}
                  </>
                ) : (
                  <>
                    {sentCampaign.sent_count} sent
                    {sentCampaign.failed_count > 0 && <span className="text-red-600"> · {sentCampaign.failed_count} failed</span>}
                    {sentCampaign.opted_out_count > 0 && <span> · {sentCampaign.opted_out_count} opted out</span>}
                  </>
                )}
              </p>
              {isSending && (
                <p className="text-xs text-gray-400 dark:text-dark-text-muted mt-2">
                  Emails are being sent in the background. Check History for progress.
                </p>
              )}
            </div>
          </div>

          <button
            onClick={resetForm}
            className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-text-primary bg-gray-100 dark:bg-dark-bg-tertiary hover:bg-gray-200 dark:hover:bg-dark-bg-hover rounded-lg transition-colors"
          >
            Send another campaign
          </button>
        </div>
      </div>
    );
  }

  const channelInfo = getChannelInfo();
  const ChannelIcon = channelInfo.icon;

  return (
    <div className="h-full flex flex-col">
      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowConfirmModal(false)}
          />
          
          {/* Modal */}
          <div className="relative bg-white dark:bg-dark-bg-secondary rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Header with gradient */}
            <div className={`px-6 py-5 bg-gradient-to-r ${
              channelInfo.color === 'green' 
                ? 'from-green-500 to-emerald-600' 
                : 'from-blue-500 to-blue-600'
            }`}>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Send className="w-6 h-6 text-white" />
                </div>
      <div>
                  <h3 className="text-lg font-semibold text-white">Confirm Send</h3>
                  <p className="text-sm text-white/80">Review before sending</p>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6">
              <div className="flex items-start gap-4 mb-6">
                <div className={`p-3 rounded-full ${
                  channelInfo.color === 'green'
                    ? 'bg-green-100 dark:bg-green-900/30'
                    : 'bg-blue-100 dark:bg-blue-900/30'
                }`}>
                  <ChannelIcon className={`w-6 h-6 ${
                    channelInfo.color === 'green'
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-blue-600 dark:text-blue-400'
                  }`} />
                </div>
                <div className="flex-1">
                  <p className="text-gray-900 dark:text-dark-text-primary font-medium mb-1">
                    You're about to send a {channelInfo.label} message
                  </p>
                  <p className="text-gray-600 dark:text-dark-text-muted text-sm">
                    This action cannot be undone. Please confirm you want to proceed.
                  </p>
                </div>
              </div>

              {/* Stats summary */}
              <div className="bg-gray-50 dark:bg-dark-bg-tertiary rounded-xl p-4 mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-900 dark:text-dark-text-primary">
                      {preview?.eligible_count || 0}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-dark-text-muted">Recipients</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-900 dark:text-dark-text-primary flex items-center justify-center gap-1">
                      <ChannelIcon className="w-6 h-6" />
                    </div>
                    <div className="text-sm text-gray-500 dark:text-dark-text-muted">{channelInfo.label}</div>
                  </div>
      </div>
                
              </div>

              {/* Warning */}
              <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 text-sm mb-6">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                <span>Messages will be sent immediately after confirmation.</span>
              </div>

              {/* Action buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-dark-border text-gray-700 dark:text-dark-text-primary rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmSend}
                  className={`flex-1 px-4 py-2.5 text-white rounded-lg transition-colors font-medium flex items-center justify-center gap-2 ${
                    channelInfo.color === 'green'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  <Send className="w-4 h-4" />
                  Send Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mx-6 mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-4xl mx-auto">
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Target Audience */}
          <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
              Target Audience
            </label>
            <div ref={audienceDropdownRef} className="relative">
              <button
                type="button"
                onClick={() => setAudienceDropdownOpen(!audienceDropdownOpen)}
                className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary text-sm text-left hover:border-gray-400 dark:hover:border-gray-500 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-gray-400" />
                  <span className={selectedListId === null ? 'text-gray-400' : ''}>
                    {selectedListId === null 
                      ? 'Select audience...'
                      : selectedListId === ''
                        ? 'All Customers'
                        : lists.find(l => l.id === selectedListId)?.name}
                  </span>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${audienceDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {audienceDropdownOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden">
                  <div className="max-h-60 overflow-y-auto">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedListId('');
                        setAudienceDropdownOpen(false);
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left transition-colors ${
                        selectedListId === '' 
                          ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' 
                          : 'text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700'
                      }`}
                    >
                      <Users className={`w-4 h-4 ${selectedListId === '' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`} />
                      <span className="flex-1">All Customers</span>
                      {selectedListId === '' && <Check className="w-4 h-4" />}
                    </button>
                    {lists.map((list) => (
                      <button
                        key={list.id}
                        type="button"
                        onClick={() => {
                          setSelectedListId(list.id);
                          setAudienceDropdownOpen(false);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left transition-colors ${
                          selectedListId === list.id 
                            ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' 
                            : 'text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700'
                        }`}
                      >
                        <FileText className={`w-4 h-4 ${selectedListId === list.id ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`} />
                        <span className="flex-1">{list.name}</span>
                        {selectedListId === list.id && <Check className="w-4 h-4" />}
                      </button>
                    ))}
                    
                    {/* Separator and Create New Audience button */}
                    <hr className="my-2 border-gray-200 dark:border-dark-border" />
                    <button
                      type="button"
                      onClick={() => {
                        setAudienceDropdownOpen(false);
                        navigate('/dashboard/audience?create=true');
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Create New Audience</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Template Selection - filtered by current channel */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
              Use Template (optional)
            </label>
            {templates.filter((t) => t.channel === channel).length > 0 ? (
              <div ref={templateDropdownRef} className="relative">
                <button
                  type="button"
                  onClick={() => setTemplateDropdownOpen(!templateDropdownOpen)}
                  className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary text-sm text-left hover:border-gray-400 dark:hover:border-gray-500 transition-colors"
                >
                  <span className={selectedTemplateId ? '' : 'text-gray-500 dark:text-dark-text-muted'}>
                    {selectedTemplateId 
                      ? templates.find(t => t.id === selectedTemplateId)?.name 
                      : 'Write custom message'}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${templateDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {templateDropdownOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden">
                    <div className="max-h-60 overflow-y-auto">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedTemplateId('');
                          setTemplateDropdownOpen(false);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                          !selectedTemplateId 
                            ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' 
                            : 'text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700'
                        }`}
                      >
                        {!selectedTemplateId && <Check className="w-4 h-4" />}
                        <span className={!selectedTemplateId ? '' : 'ml-6'}>Write custom message</span>
                      </button>
                      {templates
                        .filter((template) => template.channel === channel)
                        .map((template) => (
                          <button
                            key={template.id}
                            type="button"
                            onClick={() => {
                              setSelectedTemplateId(template.id);
                              setTemplateDropdownOpen(false);
                            }}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                              selectedTemplateId === template.id 
                                ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' 
                                : 'text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700'
                            }`}
                          >
                            {selectedTemplateId === template.id && <Check className="w-4 h-4" />}
                            <span className={selectedTemplateId === template.id ? '' : 'ml-6'}>{template.name}</span>
                          </button>
                        ))}

                      {/* Separator and Create Template button */}
                      <hr className="my-2 border-gray-200 dark:border-dark-border" />
                      <button
                        type="button"
                        onClick={() => {
                          setTemplateDropdownOpen(false);
                          navigate(`/dashboard/templates/new?channel=${channel}`);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Create {getChannelLabel(channel)} Template</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => navigate(`/dashboard/templates/new?channel=${channel}`)}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 dark:border-dark-border rounded-lg text-sm text-gray-600 dark:text-dark-text-muted hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create {getChannelLabel(channel)} Template
              </button>
            )}
          </div>

              {/* Channel - Pill buttons matching Templates/Lists design */}
          <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
              Message Type
            </label>
                <div className="flex gap-3">
                  <label
                    className={`flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer transition-colors ${
                      channel === 'sms'
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                        : 'border-gray-300 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-primary'
                    }`}
                  >
                <input
                  type="radio"
                  checked={channel === 'sms'}
                  onChange={() => handleChannelChange('sms')}
                      className="sr-only"
                />
                    <MessageSquare className="w-4 h-4" />
                    <span className="text-sm font-medium">SMS</span>
              </label>
                  <label
                    className={`flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer transition-colors ${
                      channel === 'email_plain'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                        : 'border-gray-300 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-primary'
                    }`}
                  >
                <input
                  type="radio"
                  checked={channel === 'email_plain'}
                  onChange={() => handleChannelChange('email_plain')}
                      className="sr-only"
                />
                    <Mail className="w-4 h-4" />
                    <span className="text-sm font-medium">Plain Email</span>
              </label>
                  <label
                    className={`flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer transition-colors ${
                      channel === 'email_html'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                        : 'border-gray-300 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-primary'
                    }`}
                  >
                <input
                  type="radio"
                  checked={channel === 'email_html'}
                  onChange={() => handleChannelChange('email_html')}
                      className="sr-only"
                />
                    <Code className="w-4 h-4" />
                    <span className="text-sm font-medium">HTML Email</span>
              </label>
            </div>
          </div>

          {/* Subject (for emails only) */}
          {channel !== 'sms' && (
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                Email Subject
              </label>
              
              {/* Floating Variable Toolbar for Subject - appears above input */}
              {focusedInput === 'subject' && variables && (
                <div 
                  className="mb-1"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <div className="inline-flex flex-wrap gap-1 bg-slate-800 rounded-lg shadow-xl p-2">
                    {Object.entries(variables.variables).map(([key, desc]) => (
                      <button
                        key={key}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          insertVariable(key);
                        }}
                        className="px-2.5 py-1 text-xs text-slate-300 bg-slate-700 hover:bg-slate-600 rounded transition-colors"
                        title={`{{${key}}}`}
                      >
                        {desc}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              <input
                ref={subjectInputRef}
                type="text"
                value={subject}
                onChange={(e) => {
                  setSubject(e.target.value);
                  subjectCursorRef.current = {
                    start: e.target.selectionStart || 0,
                    end: e.target.selectionEnd || 0,
                  };
                }}
                onFocus={() => setFocusedInput('subject')}
                onBlur={() => setFocusedInput(null)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setFocusedInput(null);
                    e.currentTarget.blur();
                  }
                }}
                onSelect={(e) => {
                  const target = e.target as HTMLInputElement;
                  subjectCursorRef.current = {
                    start: target.selectionStart || 0,
                    end: target.selectionEnd || 0,
                  };
                }}
                onKeyUp={(e) => {
                  const target = e.target as HTMLInputElement;
                  subjectCursorRef.current = {
                    start: target.selectionStart || 0,
                    end: target.selectionEnd || 0,
                  };
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg dark:bg-dark-bg-tertiary dark:text-dark-text-primary focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                placeholder="e.g., Hello {{pet_owner_first_name}}, from {{practice_name}}"
              />
            </div>
          )}

          {/* Message Body */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
              Message
            </label>
            
            {/* Floating Variable Toolbar for Message - appears above textarea */}
            {focusedInput === 'message' && variables && (
              <div 
                className="mb-1"
                onMouseDown={(e) => e.preventDefault()}
              >
                <div className="inline-flex flex-wrap gap-1 bg-slate-800 rounded-lg shadow-xl p-2">
                  {Object.entries(variables.variables).map(([key, desc]) => (
                    <button
                      key={key}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        insertVariable(key);
                      }}
                      className="px-2.5 py-1 text-xs text-slate-300 bg-slate-700 hover:bg-slate-600 rounded transition-colors"
                      title={`{{${key}}}`}
                    >
                      {desc}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            <textarea
              ref={messageTextareaRef}
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
                cursorPositionRef.current = {
                  start: e.target.selectionStart,
                  end: e.target.selectionEnd,
                };
              }}
              onFocus={() => setFocusedInput('message')}
              onBlur={() => setFocusedInput(null)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setFocusedInput(null);
                  e.currentTarget.blur();
                }
              }}
              onSelect={(e) => {
                const target = e.target as HTMLTextAreaElement;
                cursorPositionRef.current = {
                  start: target.selectionStart,
                  end: target.selectionEnd,
                };
              }}
              onKeyUp={(e) => {
                const target = e.target as HTMLTextAreaElement;
                cursorPositionRef.current = {
                  start: target.selectionStart,
                  end: target.selectionEnd,
                };
              }}
              required
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg dark:bg-dark-bg-tertiary dark:text-dark-text-primary focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
              placeholder={
                channel === 'email_html'
                  ? '<html>\n<body>\n  <h1>Hello {{pet_owner_first_name}}!</h1>\n  <p>Your message here...</p>\n</body>\n</html>'
                  : 'Hi {{pet_owner_first_name}}, this is a message from {{practice_name}}...'
              }
            />
            
            {channel === 'sms' && (
              <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-1">
                SMS messages over 160 characters will be sent as multiple segments.
                Current length: {body.length} characters
              </p>
            )}
          </div>
        </div>

        {/* Right Column - Preview & Send */}
            <div className="space-y-6">
          {/* Recipient Preview */}
              <div className="bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-xl p-5">
                <h3 className="font-medium text-gray-900 dark:text-dark-text-primary mb-4">Recipients</h3>

            {previewLoading ? (
              <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
              </div>
            ) : preview ? (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-dark-text-muted">Total Matching</span>
                      <span className="font-medium text-gray-900 dark:text-dark-text-primary">{preview.total_matching}</span>
                </div>
                <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-dark-text-muted">Eligible to Receive</span>
                      <span className="font-bold text-green-600 dark:text-green-400">{preview.eligible_count}</span>
                </div>
                <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-dark-text-muted">Opted Out / No Contact</span>
                      <span className="font-medium text-amber-600 dark:text-amber-400">{preview.opted_out_count}</span>
                </div>

                {preview.sample_recipients.length > 0 && (
                      <div className="pt-3 border-t border-gray-200 dark:border-dark-border">
                        <p className="text-xs text-gray-500 dark:text-dark-text-muted mb-2">Sample recipients:</p>
                    <div className="space-y-1">
                      {preview.sample_recipients.slice(0, 3).map((r) => (
                            <div key={r.id} className="text-sm text-gray-700 dark:text-dark-text-secondary truncate">
                          {r.name}
                        </div>
                      ))}
                      {preview.sample_recipients.length > 3 && (
                            <div className="text-xs text-gray-400 dark:text-dark-text-muted">
                          +{preview.sample_recipients.length - 3} more...
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
                  <p className="text-sm text-gray-500 dark:text-dark-text-muted">Unable to load preview</p>
            )}
          </div>

          {/* Send Button */}
          <button
                onClick={handleSendClick}
            disabled={sending || !body.trim() || !preview?.eligible_count}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {sending ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Send to {preview?.eligible_count || 0} Recipients
              </>
            )}
          </button>

          {!preview?.eligible_count && (
                <p className="text-xs text-center text-amber-600 dark:text-amber-400">
              No eligible recipients found. Check your list filters or ensure customers have valid contact info.
            </p>
          )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignComposer;
